/**
 * Pure computation functions for the Rider Skills Rubric feature.
 *
 * - computeGroupReadiness: derives group readiness recommendation
 * - getLatestAssessments: extracts latest assessment per skill
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.7, 4.8
 */

import type { SkillAssessment, SkillConfig, GroupReadiness, TimeTrialZone } from '../types/skills';
import type { Group } from '../api/rest-client';
import { applyTimeTrialAdjustment } from './timeTrialUtils';

/**
 * Compute group readiness recommendation for a rider based on their assessments.
 *
 * Uses fixed Sending It (Advanced) percentage thresholds:
 * - < 50% assessed → "Insufficient assessments"
 * - ≥ 75% Sending It + top group → "Top group — mastery achieved"
 * - ≥ 75% Sending It → "Ready to advance" (target = next higher group)
 * - ≥ 50% Sending It → "On track"
 * - < 50% Sending It → "Needs more time"
 *
 * When timeTrialZone is provided, the base suggestion is adjusted via applyTimeTrialAdjustment.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 4.1, 4.2, 4.3
 */
export function computeGroupReadiness(
  skills: SkillConfig[],
  latestAssessments: Map<string, SkillAssessment>,
  groups: Group[],
  currentGroup: string,
  timeTrialZone?: TimeTrialZone
): GroupReadiness {
  const totalCount = skills.length;
  const assessedCount = latestAssessments.size;

  if (totalCount === 0 || assessedCount < totalCount / 2) {
    return { suggestion: 'Insufficient assessments', assessedCount, totalCount, masteryPercent: 0 };
  }

  const masteryCount = [...latestAssessments.values()].filter(a => a.assessment_level === 'Advanced').length;
  const masteryPercent = (masteryCount / assessedCount) * 100;

  const sortedGroups = [...groups].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const currentIdx = sortedGroups.findIndex(g => g.name === currentGroup);
  const currentOrder = sortedGroups[currentIdx]?.order ?? 999;

  let baseSuggestion: string;
  let targetGroup: string | undefined;

  if (masteryPercent >= 75) {
    if (currentOrder === 1) {
      baseSuggestion = 'Top group — mastery achieved';
    } else {
      // Find the group immediately above (closest lower order number)
      const nextHigher = [...sortedGroups].reverse().find(g => (g.order ?? 999) < currentOrder);
      baseSuggestion = 'Ready to advance';
      targetGroup = nextHigher?.name;
    }
  } else if (masteryPercent >= 50) {
    baseSuggestion = 'On track';
  } else {
    baseSuggestion = 'Needs more time';
  }

  // Apply time-trial zone adjustment if provided
  if (timeTrialZone) {
    const { suggestion: adjusted, adjustment } = applyTimeTrialAdjustment(baseSuggestion, timeTrialZone);
    return {
      suggestion: adjusted as GroupReadiness['suggestion'],
      targetGroup,
      assessedCount,
      totalCount,
      masteryPercent,
      timeTrialAdjustment: adjustment,
    };
  }

  return { suggestion: baseSuggestion as GroupReadiness['suggestion'], targetGroup, assessedCount, totalCount, masteryPercent };
}

/**
 * Extract the latest assessment per skill from an array of assessments.
 * Groups by skill_id and returns the one with the most recent assessed_at.
 *
 * Requirements: 5.2, 5.6
 */
export function getLatestAssessments(assessments: SkillAssessment[]): Map<string, SkillAssessment> {
  const latest = new Map<string, SkillAssessment>();

  for (const assessment of assessments) {
    const existing = latest.get(assessment.skill_id);
    if (!existing || assessment.assessed_at.localeCompare(existing.assessed_at) > 0) {
      latest.set(assessment.skill_id, assessment);
    }
  }

  return latest;
}
